import React from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { prompts } from "./TaxHelpers";

/* ---------------- Component ---------------- */

/**
 * Renders the disabled/placeholder state for any tax configuration tab.
 * Also renders placeholder content for TDS, LWF, and Statutory Bonus.
 * @param {object} props
 * @param {'epf' | 'esi' | 'tds' | 'professional_tax' | 'lwf' | 'statutory_bonus'} props.activeTab - The currently active tab ID.
 * @param {function} props.handleEnable - Function to enable the configuration.
 * @param {function} props.handleSave - Function to save the configuration (for placeholders).
 * @param {function} props.handleCancel - Function to cancel the edit (for placeholders).
 * @param {function} props.handleDisable - Function to disable the configuration (for placeholders).
 * @param {string} props.savingSection - The section currently being saved.
 * @param {array} props.tabs - Array of tab objects for finding the label.
 * @returns {JSX.Element}
 */
const GeneralConfiguration = ({
  activeTab,
  handleEnable,
  tabs,
  canEditTaxConfig,
}) => {
  const prompt = prompts[activeTab];

  if (!prompt) {
    return <div className="text-red-500">Invalid tab configuration.</div>;
  }

  // --- Disabled State
  const buttonLabel =
    activeTab === "professional_tax"
      ? "Add Work Locations"
      : `Enable ${tabs.find((t) => t.id === activeTab)?.label}`;

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center max-w-2xl">
        <div className="mb-6">
          <div className="w-48 h-48 mx-auto bg-primary/20 border border-primary/30 rounded-full flex items-center justify-center">
            <span className="text-6xl text-primary">
              {prompt.illustration}
            </span>
          </div>
        </div>

        <h3 className="dark:text-stone-50 text-2xl font-semibold mb-4">
          {prompt.title}
        </h3>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {prompt.description}
        </p>

        <Button
          onClick={() => {
            if (!canEditTaxConfig) return;
            handleEnable(activeTab);
          }}
          disabled={!canEditTaxConfig}
          className="bg-primary hover:bg-primary/90 text-white dark:text-gray-50"
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

export default GeneralConfiguration;
